<template>
  <nav
    ref="nav"
    class="related-products"
  >
    <div>
      <h3>
        <span>{{ $content.navTitle }}</span>
        <strong>{{ $currentCollection.genie }}</strong>
      </h3>
      <div
        v-if="needsScrolling"
        role="navigation"
      >
        <div
          class="previous"
          :class="{ disabled: index === 0 }"
          @click="index = Math.max(0, index - 1)"
        >
          <ArrowIcon />
        </div>
        <div
          class="next"
          :class="{ disabled: (index === related.length - 1 ) || hasScrolledToEnd }"
          @click="index = Math.min(related.length - 1, index + 1)"
        >
          <ArrowIcon />
        </div>
      </div>
    </div>
    <menu
      ref="menu"
      :style="`--current: ${index};`"
    >
      <ProductListCard
        v-for="(prod) in related"
        :key="prod.id"
        :product="prod"
        :short-theme="false"
      />
    </menu>
  </nav>
</template>
<script>
import ProductListCard from '@/components/common/ProductListCard.vue';
import ArrowIcon from '@/assets/images/common/icons/arrow.svg?inline';
import debounce from 'lodash/debounce';

export default {
  name: 'RelatedProductsNavigation',
  components: {
    ArrowIcon,
    ProductListCard,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      index: 0,
      needsScrolling: true,
      hasScrolledToEnd: false,
    };
  },
  computed: {
    /** return all product from the collection, excluding the current product */
    related() {
      const col = this.$currentCollection;
      return col.products.filter(prod => prod.id !== this.product.id);
    },
  },
  watch: {
    /** on Index change, slide the menu to the corresponding product card */
    index() {
      this.computeNeedsScrolling();
    },
  },
  mounted() {
    this.__debouncedOnResize = debounce(this.onResize, 200);
    window.addEventListener('resize', this.__debouncedOnResize);
    this.computeNeedsScrolling();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.__debouncedOnResize);
  },
  methods: {
    onResize() {
      this.computeNeedsScrolling();
      this.$forceUpdate();
    },
    computeNeedsScrolling() {
      // Remove left and right padding from nav width for calculation
      const nav = this.$refs.nav;
      const menu = this.$refs.menu;
      if (!nav || !menu) return;
      const style = window.getComputedStyle(nav);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      // const paddingRight = parseFloat(style.paddingRight) || 0;
      const navWidth = nav.offsetWidth - paddingLeft;
      this.needsScrolling = navWidth < menu.offsetWidth;
      if (this.needsScrolling) {
        this.hasScrolledToEnd = this.computeScrolledToEnd();
      }
    },
    /** Compute if menu has translated to the end */
    computeScrolledToEnd() {
      const nav = this.$refs.nav;
      const menu = this.$refs.menu;
      if (!nav || !menu) return false;

      const style = window.getComputedStyle(nav);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const navWidth = nav.offsetWidth - paddingLeft;
      const menuWidth = menu.offsetWidth;

      // Get CSS variable values
      const carouselGap = parseFloat(getComputedStyle(menu).getPropertyValue('--carousel-gap')) || 15;
      const itemWidth = parseFloat(getComputedStyle(menu).getPropertyValue('--carousel-item-width')) || 240;

      // Calculate maxTranslateX using the same logic as CSS
      const maxTranslateX = Math.min(0, (navWidth - menuWidth) - carouselGap);

      // Calculate current translateX based on index
      const translateX = this.index * -(itemWidth + carouselGap);

      // Check if we've reached or passed the maximum translation
      return translateX <= maxTranslateX;
    },
  },
};
</script>

<style lang="stylus" scoped>
.related-products {
  padding: 80px 50px 40px 50px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-color);
  background: var(--main-color);

  +mobile() {
    overflow: hidden;
    padding: 80px 20px 40px;
  }

  --container-width: 100vw;
  --carousel-gap: 15px;
  --carousel-item-width: 240px;

  +desktop() {
    --carousel-gap: 30px;
    --carousel-item-width: 240px;
  }

  div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: fit-content;

    h3 {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-transform: none;
      font-size: 22px;
      font-style: italic;
      font-weight: 400;
      strong {
        font-weight: 700;
      }
    }

    div[role="navigation"] {
      display: flex;
      gap: 15px;
      margin-left: 20px;

      .previous, .next {
        padding: 8px;
        width: 36px;
        height: 36px;

        border-radius: 50%;
        border: 1px solid var(--white-color);
        background: var(--white-color);
        cursor: pointer;
        transition: all 0.3s ease;

        svg {
          transition: all 0.3s ease;
          * {
            stroke: var(--inverted-text-color);
            transition: all 0.3s ease;
          }
        }

        &.previous {
          transform: rotate(180deg);
        }

        &.disabled {
          opacity: 0.6;
          cursor: default;
          background: transparent;
          pointer-events: none;
          svg * {
            stroke: var(--text-color);
          }
        }

        +mouseDevice() {
          &:hover {
            background: var(--main-color);
            svg {
              transform: translateX(2px);
              * {
                stroke: var(--white-color);
              }
            }
          }
        }
      }
    }
  }

  menu {
    display: flex;
    margin: 50px 0;
    padding: 0;
    list-style: none;
    width: fit-content;
    gap: var(--carousel-gap);

    transition: transform 0.5s ease-out;
    +mobile() {
      margin: 30px 0;
    }

    li {
      width: var(--carousel-item-width);
    }
  }
}
</style>
<style scoped>
.related-products menu {
    /* Offset and app-gutter are 0 becausethe container already acounts for them with padding and we pass in the raw menu width */
    --offset: 0px;
    --app-gutter: 25px;

    /* Calculate horizontal translation based on current slide index
       Formula: (current_index * -(item_width + gap)) + optional_offset
       Negative value moves the slider left, offset can fine-tune position */
    --translateX: calc(calc(var(--current) * calc(-1 * calc(var(--carousel-item-width) + var(--carousel-gap)))) + var(--offset, 0px));

    /* Maximum available width for the slider container
       Takes the smaller value between container width and viewport width minus app gutters */
    --max-width: min(var(--container-width), calc(100vw - var(--app-gutter, 0px)));

    /* Actual usable slider width after accounting for left margin */
    --slider-width: calc(var(--max-width) - var(--leftMargin, 0px));

    /* Maximum allowed translation to prevent sliding past the last item
       Formula: min(0px, (slider_width - 100%) - gap)
       Clamped to 0 to prevent positive offset when slider is shorter than container */
    --maxTranslateX: min(0px, calc(calc(var(--slider-width) - 100%) - var(--carousel-gap)));

    /* Final target translation value, clamped to maxTranslateX
       Uses max() to prevent sliding beyond the last item (more negative values are clamped) */
    --targetX: max(var(--translateX), var(--maxTranslateX));

    /* Apply the calculated translation to move the slider horizontally */
    transform: translateX(var(--targetX));
  }

</style>
